import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { GroupedModules, Module, StorageKeyEnum, UserAuthData } from '../models/auth';
import { Router } from '@angular/router';
import { ModulesEnum, ModulesGroupingEnum } from '../models/modules';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private userAuthData: BehaviorSubject<UserAuthData | null> =
    new BehaviorSubject<UserAuthData | null>(null);

  private selectedBusinessUid: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  private _availableModulesGroups$: BehaviorSubject<GroupedModules[] | null> = new BehaviorSubject<GroupedModules[] | null>(null);
  readonly availableModulesGroups$ = this._availableModulesGroups$.asObservable();

  constructor(private router: Router) {
    this.init();
  }

  init(): void {
    const localUserData = localStorage.getItem(StorageKeyEnum.UserData);
    if (localUserData) {
      const userAuthData = JSON.parse(localUserData);
      this.userAuthData.next(userAuthData);
      this.setAvailableModules(userAuthData);
    }
  }

  setAvailableModules(userAuthData: UserAuthData) {
    // TODO: This is Matt's perspective - need to confirm with Ops
    const modules: Module[] = [];
    const financialModules: Module[] = [];
    const generalModules: Module[] = [];

    modules.push(
      {
        Module: ModulesEnum.Dashboard,
        ModuleNiceName: 'Dashboard',
        RouterLink: '/dashboard',
        AccessLevel: 'write',
        ModuleIcon: 'dashboard',
        ChildRoutes: [],
        DisplayName: 'Dashboard'
      }
    )

    if (userAuthData.User.IsSuperUser) {
      modules.push(
        {
          Module: ModulesEnum.Zones,
          ModuleNiceName: 'Zones & Schedules',
          RouterLink: '/dashboard/zones',
          AccessLevel: 'write',
          ModuleIcon: 'schedule',
          ChildRoutes: [],
          DisplayName: 'Zones'
        }
      )

      modules.push({
        Module: ModulesEnum.OrderManagement,
        ModuleNiceName: 'Order Management',
        RouterLink: '/dashboard/ordermanagement',
        AccessLevel: 'write',
        ModuleIcon: 'directions_car',
        ChildRoutes: [],
        DisplayName: 'Order Management'
      },
      {
        Module: ModulesEnum.DriverManagement,
        ModuleNiceName: 'Driver Management',
        RouterLink: '/dashboard/drivermanagement',
        AccessLevel: 'write',
        ModuleIcon: 'directions_car',
        ChildRoutes: [],
        DisplayName: 'Driver Management'

      })

    }

    if (userAuthData.User.IsSystemAdministrator) {
      generalModules.push(
        {
          Module: ModulesEnum.Admin,
          ModuleNiceName: 'Admin',
          RouterLink: '/dashboard/admin',
          AccessLevel: 'write',
          ModuleIcon: 'settings',
          ChildRoutes: [],
          DisplayName: 'Admin'
        }
      )
      modules.push(
        {
          Module: ModulesEnum.Reports,
          ModuleNiceName: 'Reports',
          RouterLink: '/dashboard/reports',
          AccessLevel: 'write',
          ModuleIcon: 'note',
          ChildRoutes: [],
          DisplayName: 'Reports'
        }, {
        Module: ModulesEnum.CartrackTracker,
        ModuleNiceName: 'Cartrack Tracker',
        RouterLink: '/dashboard/cartracktracker',
        AccessLevel: 'write',
        ModuleIcon: 'directions_car',
        ChildRoutes: [],
        DisplayName: 'Cartrack Tracker'
      },
      )
    }
    if (userAuthData?.Vendors?.length > 0) {
      if (userAuthData.User.SuperUserAccessLevelId === 8) {
        financialModules.push(
          {
            Module: ModulesEnum.Financials,
            ModuleNiceName: 'Payment Runs',
            RouterLink: '/dashboard/financials/payment-runs',
            AccessLevel: 'write',
            ModuleIcon: 'receipt_long',
            ChildRoutes: [],
            DisplayName: 'Payment Runs'
          },
          {
            Module: ModulesEnum.Banking,
            ModuleNiceName: 'Banking',
            RouterLink: '/dashboard/banking',
            AccessLevel: 'write',
            ModuleIcon: 'directions_car',
            ChildRoutes: [],
            DisplayName: 'Banking'
          }
        )
      }
      financialModules.push(
        {
          Module: ModulesEnum.Financials,
          ModuleNiceName: 'Financials',
          RouterLink: '/dashboard/financials',
          AccessLevel: 'write',
          ModuleIcon: 'payments',
          ChildRoutes: [],
          DisplayName: 'Payments and Approvals'
        },
        {
          Module: ModulesEnum.Financials,
          ModuleNiceName: 'Driver Ledgers',
          RouterLink: '/dashboard/financials/ledgers',
          AccessLevel: 'write',
          ModuleIcon: 'account_balance_wallet',
          ChildRoutes: [],
          DisplayName: 'Driver Ledgers'
        }
      )
    }
    this._availableModulesGroups$.next(
      [
        {
          GroupingName: ModulesGroupingEnum.planning,
          Modules: modules,
          Expanded: true
        },
        {
          GroupingName: ModulesGroupingEnum.financials,
          Modules: financialModules
        },
        {
          GroupingName: ModulesGroupingEnum.general,
          Modules: generalModules
        }
      ],
    );
  }

  get authUserData$(): Observable<UserAuthData | null> {
    return new Observable((f) => {
      this.userAuthData.subscribe(f);
    });
  }

  setUserAuthData(data: UserAuthData): void {
    localStorage.setItem(StorageKeyEnum.UserData, JSON.stringify(data));
    this.userAuthData.next(data);
  }

  setSelectedBusiness(data: string | null): void {
    this.selectedBusinessUid.next(data);
  }

  canViewAdmin() {
    const value = this.userAuthData.getValue();
    return value ? value.User.IsSystemAdministrator : false;
  }

  isLoggedIn(): boolean {
    const value = this.userAuthData.getValue();
    return !!value;
  }

  logOut() {
    localStorage.removeItem(StorageKeyEnum.UserData);
    localStorage.removeItem(StorageKeyEnum.AuthToken);
    this.router.navigate(['/auth']);
  }

  setActiveModule(grouping: GroupedModules, module: Module) {
    // Get a snapshot of the current state of module groups
    const groupedModules = this._availableModulesGroups$.getValue();

    if (groupedModules === null) { return; }

    // set all modules for all groups to inactive
    groupedModules.map((group) => {
      group.Modules.map((m) => {
        m.isActive = false;
      });
    });

    // set the active module
    const activeGroup = groupedModules.find((group) => group.GroupingName === grouping.GroupingName);
    if (activeGroup) {
      const activeModule = activeGroup.Modules.find((m) => m.RouterLink === module.RouterLink);
      if (activeModule) {
        activeModule.isActive = true;
      }
    }

    // update the observable
    this._availableModulesGroups$.next(groupedModules);

  }
}
